import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Sun, Shield, TreePine, ShoppingBag, Utensils, Mountain, GraduationCap, BookOpen, Trophy, Star } from 'lucide-react';
import { ContactCTA } from '../components/ContactCTA';
import { ListingsComingSoon } from '../components/ListingsComingSoon';
const placeholderListings = [{
  id: 1,
  price: '$1,850,000',
  address: '12 Augusta Canyon Way',
  area: 'Southern Highlands Golf Club',
  beds: 5,
  baths: 5,
  sqft: '4,600',
  image: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?q=80&w=2940&auto=format&fit=crop'
}, {
  id: 2,
  price: '$725,000',
  address: '4891 Montecito Dr',
  area: 'Southern Highlands',
  beds: 4,
  baths: 3,
  sqft: '2,850',
  image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=2940&auto=format&fit=crop'
}, {
  id: 3,
  price: '$1,150,000',
  address: '11 Promontory Pointe Ln',
  area: 'Southern Highlands',
  beds: 4,
  baths: 4.5,
  sqft: '3,900',
  image: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?q=80&w=2950&auto=format&fit=crop'
}];
export function SouthernHighlandsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <main className="bg-white" data-id="element-663">
      {/* Hero */}
      <section className="relative h-[70vh] min-h-[600px] w-full flex items-center justify-center overflow-hidden" data-id="element-664">
        <div className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat" style={{
        backgroundImage: 'url("https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?q=80&w=2950&auto=format&fit=crop")'
      }} data-id="element-665">
          <div className="absolute inset-0 bg-charcoal/40 mix-blend-multiply" data-id="element-666"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-charcoal/60 via-transparent to-charcoal/80" data-id="element-667"></div>
        </div>
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto mt-20" data-id="element-668">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1
        }} data-id="element-669">
            <span className="text-gold uppercase tracking-[0.3em] text-sm font-medium mb-6 block" data-id="element-670">
              Community Spotlight
            </span>
            <h1 className="text-5xl md:text-7xl text-white font-serif mb-6 leading-tight" data-id="element-671">
              Southern Highlands
            </h1>
            <p className="text-lg md:text-xl text-white/90 font-light max-w-2xl mx-auto" data-id="element-672">
              An exclusive gated enclave featuring championship golf and
              Mediterranean-inspired luxury estates.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Overview & Stats */}
      <section className="py-24 px-6 bg-white" data-id="element-673">
        <div className="max-w-7xl mx-auto" data-id="element-674">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-675">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-676">
              <h2 className="text-3xl md:text-4xl font-serif text-charcoal mb-6" data-id="element-677">
                Where Elegance Meets{' '}
                <span className="italic text-gold" data-id="element-678">Exclusivity</span>
              </h2>
              <div className="space-y-4 text-charcoal-light font-light leading-relaxed text-lg" data-id="element-679">
                <p data-id="element-680">
                  Southern Highlands is one of the most prestigious
                  master-planned communities in Las Vegas, located in the
                  southern tip of the valley along the I-15 corridor. Developed
                  in the late 1990s, this guard-gated community spans over 2,300
                  acres and is home to approximately 7,000 residents who enjoy
                  an unparalleled level of privacy and luxury.
                </p>
                <p data-id="element-681">
                  The community is anchored by the Southern Highlands Golf Club,
                  a stunning 18-hole championship course designed by Robert
                  Trent Jones Sr. and Jr. Homes range from elegant $500K
                  residences to breathtaking $5M+ custom estates with sweeping
                  mountain and city views.
                </p>
                <p data-id="element-682">
                  Southern Highlands is known for its Mediterranean-inspired
                  architecture, lush landscaping, and resort-style amenities
                  that create a country club lifestyle without ever leaving
                  home.
                </p>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="grid grid-cols-2 gap-6" data-id="element-683">
              <div className="bg-warmGray p-8 text-center border border-warmGray-dark" data-id="element-684">
                <Trophy className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-685" />
                <h4 className="text-3xl font-serif text-charcoal mb-2" data-id="element-686">18</h4>
                <p className="text-sm text-charcoal-light uppercase tracking-wider" data-id="element-687">
                  Hole Golf Course
                </p>
              </div>
              <div className="bg-warmGray p-8 text-center border border-warmGray-dark" data-id="element-688">
                <Shield className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-689" />
                <h4 className="text-3xl font-serif text-charcoal mb-2" data-id="element-690">24/7</h4>
                <p className="text-sm text-charcoal-light uppercase tracking-wider" data-id="element-691">
                  Guard Gated
                </p>
              </div>
              <div className="bg-warmGray p-8 text-center border border-warmGray-dark" data-id="element-692">
                <MapPin className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-693" />
                <h4 className="text-3xl font-serif text-charcoal mb-2" data-id="element-694">20m</h4>
                <p className="text-sm text-charcoal-light uppercase tracking-wider" data-id="element-695">
                  To The Strip
                </p>
              </div>
              <div className="bg-warmGray p-8 text-center border border-warmGray-dark" data-id="element-696">
                <Star className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-697" />
                <h4 className="text-3xl font-serif text-charcoal mb-2" data-id="element-698">Top</h4>
                <p className="text-sm text-charcoal-light uppercase tracking-wider" data-id="element-699">
                  Luxury Community
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Golf & Country Club */}
      <section className="py-24 bg-warmGray px-6 border-y border-warmGray-dark" data-id="element-700">
        <div className="max-w-7xl mx-auto" data-id="element-701">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-702">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="order-2 lg:order-1" data-id="element-703">
              <div className="relative h-[500px]" data-id="element-704">
                <img src="https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?q=80&w=2940&auto=format&fit=crop" alt="Golf Course" className="w-full h-full object-cover" data-id="element-705" />
                <div className="absolute inset-0 border border-gold/30 m-4 pointer-events-none" data-id="element-706"></div>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="order-1 lg:order-2" data-id="element-707">
              <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-708">
                The Centerpiece
              </span>
              <h2 className="text-3xl md:text-4xl font-serif text-charcoal mb-6" data-id="element-709">
                Southern Highlands Golf Club
              </h2>
              <div className="space-y-4 text-charcoal-light font-light leading-relaxed text-lg" data-id="element-710">
                <p data-id="element-711">
                  The crown jewel of the community, Southern Highlands Golf Club
                  features an 18-hole championship course designed by the
                  legendary Robert Trent Jones Sr. and Jr. The course winds
                  through the natural desert terrain with dramatic elevation
                  changes and stunning mountain views.
                </p>
                <p data-id="element-712">
                  Members enjoy a 74,000 square foot Mediterranean-style
                  clubhouse with fine dining, a world-class spa, tennis courts,
                  fitness center, and resort-style swimming pools. It's a
                  lifestyle destination that rivals the finest country clubs in
                  the nation.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Lifestyle & Amenities */}
      <section className="py-24 px-6 bg-white" data-id="element-713">
        <div className="max-w-7xl mx-auto" data-id="element-714">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-715">
            <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-716">
              Resort-Style Living
            </span>
            <h2 className="text-4xl font-serif text-charcoal" data-id="element-717">
              Lifestyle & Amenities
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-718">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="group" data-id="element-719">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-720">
                <img src="https://images.unsplash.com/photo-1540539234-c14a20fb7c7b?q=80&w=2940&auto=format&fit=crop" alt="Spa" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-721" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-722">
                World-Class Spa & Fitness
              </h3>
              <p className="text-charcoal-light font-light text-sm" data-id="element-723">
                A full-service spa, state-of-the-art fitness center, and
                wellness programs designed for the ultimate in relaxation and
                health.
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="group" data-id="element-724">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-725">
                <img src="https://images.unsplash.com/photo-1414235077428-338989a2e8c0?q=80&w=2940&auto=format&fit=crop" alt="Fine Dining" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-726" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-727">
                Fine Dining
              </h3>
              <p className="text-charcoal-light font-light text-sm" data-id="element-728">
                Multiple dining venues within the clubhouse offering everything
                from casual poolside fare to elegant multi-course dinners.
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} className="group" data-id="element-729">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-730">
                <img src="https://images.unsplash.com/photo-1571896349842-33c89424de2d?q=80&w=2940&auto=format&fit=crop" alt="Resort Pool" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-731" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-732">
                Resort-Style Pools
              </h3>
              <p className="text-charcoal-light font-light text-sm" data-id="element-733">
                Multiple swimming pools, cabanas, and outdoor lounging areas
                perfect for enjoying 300+ days of Las Vegas sunshine.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Nearby Attractions */}
      <section className="py-24 bg-warmGray px-6 border-y border-warmGray-dark" data-id="element-734">
        <div className="max-w-7xl mx-auto" data-id="element-735">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-736">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="space-y-8" data-id="element-737">
              <div data-id="element-738">
                <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-739">
                  Perfectly Located
                </span>
                <h2 className="text-3xl md:text-4xl font-serif text-charcoal mb-6" data-id="element-740">
                  Nearby Attractions
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-741">
                <div className="bg-white p-6 border border-warmGray-dark" data-id="element-742">
                  <ShoppingBag className="w-6 h-6 text-gold mb-3" data-id="element-743" />
                  <h4 className="font-serif text-lg text-charcoal mb-2" data-id="element-744">
                    The District at Green Valley
                  </h4>
                  <p className="text-charcoal-light font-light text-sm" data-id="element-745">
                    Upscale shopping and dining just minutes away with boutiques
                    and restaurants.
                  </p>
                </div>
                <div className="bg-white p-6 border border-warmGray-dark" data-id="element-746">
                  <Mountain className="w-6 h-6 text-gold mb-3" data-id="element-747" />
                  <h4 className="font-serif text-lg text-charcoal mb-2" data-id="element-748">
                    Sloan Canyon
                  </h4>
                  <p className="text-charcoal-light font-light text-sm" data-id="element-749">
                    National Conservation Area with ancient petroglyphs and
                    stunning desert hiking trails.
                  </p>
                </div>
                <div className="bg-white p-6 border border-warmGray-dark" data-id="element-750">
                  <Utensils className="w-6 h-6 text-gold mb-3" data-id="element-751" />
                  <h4 className="font-serif text-lg text-charcoal mb-2" data-id="element-752">
                    M Resort Spa Casino
                  </h4>
                  <p className="text-charcoal-light font-light text-sm" data-id="element-753">
                    A luxury boutique resort with fine dining, entertainment,
                    and a world-class spa.
                  </p>
                </div>
                <div className="bg-white p-6 border border-warmGray-dark" data-id="element-754">
                  <MapPin className="w-6 h-6 text-gold mb-3" data-id="element-755" />
                  <h4 className="font-serif text-lg text-charcoal mb-2" data-id="element-756">
                    Las Vegas Premium Outlets
                  </h4>
                  <p className="text-charcoal-light font-light text-sm" data-id="element-757">
                    South location offering designer brands at outlet prices,
                    just a short drive away.
                  </p>
                </div>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-758">
              <div className="relative h-[600px]" data-id="element-759">
                <img src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?q=80&w=2940&auto=format&fit=crop" alt="Mountain Views" className="w-full h-full object-cover" data-id="element-760" />
                <div className="absolute inset-0 border border-gold/30 m-4 pointer-events-none" data-id="element-761"></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Schools */}
      <section className="py-24 px-6 bg-white" data-id="element-762">
        <div className="max-w-4xl mx-auto text-center" data-id="element-763">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-764">
            <GraduationCap className="w-12 h-12 text-gold mx-auto mb-6" data-id="element-765" />
            <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-766">
              Education
            </span>
            <h2 className="text-4xl font-serif text-charcoal mb-6" data-id="element-767">
              Top-Rated Schools
            </h2>
            <p className="text-charcoal-light font-light leading-relaxed text-lg mb-12" data-id="element-768">
              Southern Highlands is zoned for some of Clark County's best
              schools, including Elise L. Wolff Elementary, Del E. Webb Middle
              School, and Coronado High School. The community also offers easy
              access to private institutions like Henderson International School
              and Bishop Gorman High School — one of the top private schools in
              Nevada.
            </p>
            <div className="flex justify-center gap-8" data-id="element-769">
              <div className="flex items-center gap-2 text-charcoal" data-id="element-770">
                <BookOpen className="w-5 h-5 text-gold" data-id="element-771" />
                <span className="font-serif" data-id="element-772">Top Public Schools</span>
              </div>
              <div className="flex items-center gap-2 text-charcoal" data-id="element-773">
                <BookOpen className="w-5 h-5 text-gold" data-id="element-774" />
                <span className="font-serif" data-id="element-775">Bishop Gorman Nearby</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <ListingsComingSoon area="Southern Highlands" data-id="element-776" />

      <ContactCTA data-id="element-777" />
    </main>;
}